import { PlanSchema, upgradePlan, type Plan } from "@roxpacer/core";

export type PutResult = { status: "saved" } | { status: "conflict"; plan: Plan };

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

export interface PlansApi {
  list(): Promise<Plan[]>;
  put(plan: Plan): Promise<PutResult>;
  delete(id: string): Promise<void>;
}

/** Returns a valid access token, or null when signed out. */
export type TokenProvider = () => Promise<string | null>;

export function createPlansApi(baseUrl: string, getToken: TokenProvider): PlansApi {
  const request = async (path: string, init: RequestInit = {}) => {
    const token = await getToken();
    if (!token) throw new ApiError(401, "Not signed in");
    const response = await fetch(`${baseUrl}${path}`, {
      ...init,
      headers: {
        ...init.headers,
        authorization: `Bearer ${token}`,
        ...(init.body ? { "content-type": "application/json" } : {}),
      },
    });
    const body = response.status === 204 ? undefined : await response.json().catch(() => ({}));
    return { status: response.status, body: body as Record<string, unknown> | undefined };
  };

  const fail = (status: number, body: Record<string, unknown> | undefined): never => {
    const message = typeof body?.error === "string" ? body.error : `Request failed (${status})`;
    throw new ApiError(status, message);
  };

  // Responses are validated like any other untrusted input.
  const parsePlan = (raw: unknown): Plan | null => {
    const result = PlanSchema.safeParse(upgradePlan(raw));
    return result.success ? result.data : null;
  };

  return {
    async list() {
      const { status, body } = await request("/plans");
      if (status !== 200) fail(status, body);
      const raw = Array.isArray(body?.plans) ? body.plans : [];
      return raw.flatMap((p) => parsePlan(p) ?? []);
    },
    async put(plan) {
      const { status, body } = await request(`/plans/${encodeURIComponent(plan.id)}`, {
        method: "PUT",
        body: JSON.stringify(plan),
      });
      if (status === 200 || status === 201) return { status: "saved" };
      const current = status === 409 ? parsePlan(body?.plan) : null;
      if (current) return { status: "conflict", plan: current };
      return fail(status, body);
    },
    async delete(id) {
      const { status, body } = await request(`/plans/${encodeURIComponent(id)}`, {
        method: "DELETE",
      });
      if (status !== 204 && status !== 404) fail(status, body);
    },
  };
}

export interface ShareCode {
  code: string;
  planId: string;
  createdAt: string;
  expiresAt: string;
}

export interface ShareApi {
  /** The plan's share code, or null if it isn't shared. */
  get(planId: string): Promise<ShareCode | null>;
  /** Issues a new code, replacing any existing one. */
  create(planId: string): Promise<ShareCode>;
  revoke(planId: string): Promise<void>;
}

function parseShare(raw: unknown): ShareCode {
  const share = (raw as { share?: Partial<ShareCode> } | undefined)?.share;
  if (
    typeof share?.code !== "string" ||
    typeof share.planId !== "string" ||
    typeof share.createdAt !== "string" ||
    typeof share.expiresAt !== "string"
  ) {
    throw new ApiError(502, "Unexpected response from the server");
  }
  return share as ShareCode;
}

export function createShareApi(baseUrl: string, getToken: TokenProvider): ShareApi {
  const request = async (planId: string, method: "GET" | "POST" | "DELETE") => {
    const token = await getToken();
    if (!token) throw new ApiError(401, "Sign in to share plans");
    const response = await fetch(`${baseUrl}/plans/${encodeURIComponent(planId)}/share-code`, {
      method,
      headers: { authorization: `Bearer ${token}` },
    });
    const body = response.status === 204 ? undefined : await response.json().catch(() => ({}));
    return { status: response.status, body };
  };
  const fail = (status: number, body: unknown): never => {
    const error = (body as { error?: unknown } | undefined)?.error;
    throw new ApiError(status, typeof error === "string" ? error : `Request failed (${status})`);
  };

  return {
    async get(planId) {
      const { status, body } = await request(planId, "GET");
      if (status !== 200) fail(status, body);
      return (body as { share?: unknown } | undefined)?.share === null ? null : parseShare(body);
    },
    async create(planId) {
      const { status, body } = await request(planId, "POST");
      if (status !== 201) fail(status, body);
      return parseShare(body);
    },
    async revoke(planId) {
      const { status, body } = await request(planId, "DELETE");
      if (status !== 204) fail(status, body);
    },
  };
}

export type SharedPlanResult =
  { status: "found"; plan: Plan; expiresAt: string } | { status: "not-found" };

/** Public: the plan behind a share code. */
export async function fetchSharedPlan(baseUrl: string, code: string): Promise<SharedPlanResult> {
  const response = await fetch(`${baseUrl}/shared-plans/${encodeURIComponent(code)}`);
  if (response.status === 404) return { status: "not-found" };
  const body = (await response.json().catch(() => ({}))) as { plan?: unknown; expiresAt?: unknown };
  if (response.status !== 200) {
    throw new ApiError(response.status, `Request failed (${response.status})`);
  }
  const plan = PlanSchema.safeParse(upgradePlan(body.plan));
  if (!plan.success || typeof body.expiresAt !== "string") {
    throw new ApiError(502, "Unexpected response from the server");
  }
  return { status: "found", plan: plan.data, expiresAt: body.expiresAt };
}
